/**
 * TrustGrid Findings Summary - Summary table of findings grouped by control
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tag, Button, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import ProjectSelector from './ProjectSelector';
import { TGDataTable, LoadingState } from '../../../components/common';
import { showError } from '../../../utils/notifications';
import { colors, spacing } from '../../../theme/designTokens';
import { handleCrossOrgError } from '../utils/errorHandlers';
import axios from 'axios';

function FindingsSummary() {
  const { selectedProjectId, selectProject } = useTrustGrid();
  const navigate = useNavigate();
  const location = useLocation();

  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    ];
    return [...baseBreadcrumbs, { label: 'Findings', path: '/trustgrid/findings' }, { label: 'Summary', path: '/trustgrid/findings/summary' }];
  }, [location.state]);

  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedProjectId) {
      fetchSummary();
    }
  }, [selectedProjectId]);

  const fetchSummary = async () => {
    if (!selectedProjectId) {
      showError('Please select a project to view findings summary');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`/api/trustgrid/v1/projects/${selectedProjectId}/findings/summary`);
      setSummary(response.data.summary || []);
    } catch (error) {
      if (error.response?.status === 404) {
        handleCrossOrgError(error, selectProject, navigate, 'FindingsSummary');
      } else {
        showError('Failed to load findings summary');
      }
      console.error('Failed to load findings summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (record, columnKey) => {
    // Create filter params based on the clicked column
    const filterParams = {
      control_code: record.control_code
    };

    // Map column to filter params
    if (columnKey === 'failures_open') {
      filterParams.finding_type = 'failure';
      filterParams.status = 'open';
    } else if (columnKey === 'failures_in_progress') {
      filterParams.finding_type = 'failure';
      filterParams.status = 'in_progress';
    } else if (columnKey === 'failures_closed') {
      filterParams.finding_type = 'failure';
      filterParams.status = 'closed';
    } else if (columnKey === 'warnings_open') {
      filterParams.finding_type = 'warning';
      filterParams.status = 'open';
    } else if (columnKey === 'warnings_closed') {
      filterParams.finding_type = 'warning';
      filterParams.status = 'closed';
    } else if (columnKey === 'due') {
      filterParams.due_filter = 'due';
    } else if (columnKey === 'overdue') {
      filterParams.due_filter = 'overdue';
    }

    // Build query string
    const queryString = new URLSearchParams(filterParams).toString();

    // Navigate to details page with filters and breadcrumbs
    navigate(`/trustgrid/findings/details?${queryString}`, {
      state: { breadcrumbs: breadcrumbs }
    });
  };

  const renderCountCell = (count, record, columnKey) => {
    // Determine color and icon based on column type
    let color = 'default';
    let icon = null;
    let tooltipText = '';

    if (count === 0) {
      return (
        <Tag color="default" style={{ cursor: 'default' }}>
          0
        </Tag>
      );
    }

    if (columnKey.startsWith('failures_')) {
      if (columnKey === 'failures_open') {
        color = colors.status.fail;
        icon = <CloseCircleOutlined />;
        tooltipText = `Click to view ${count} open failure(s)`;
      } else if (columnKey === 'failures_in_progress') {
        color = colors.status.warning;
        icon = <ExclamationCircleOutlined />;
        tooltipText = `Click to view ${count} in-progress failure(s)`;
      } else if (columnKey === 'failures_closed') {
        color = colors.status.pass;
        icon = <CheckCircleOutlined />;
        tooltipText = `Click to view ${count} closed failure(s)`;
      }
    } else if (columnKey.startsWith('warnings_')) {
      if (columnKey === 'warnings_open') {
        color = colors.status.warning;
        icon = <ExclamationCircleOutlined />;
        tooltipText = `Click to view ${count} open warning(s)`;
      } else if (columnKey === 'warnings_closed') {
        color = colors.status.pass;
        icon = <CheckCircleOutlined />;
        tooltipText = `Click to view ${count} closed warning(s)`;
      }
    }

    return (
      <Tooltip title={tooltipText}>
        <Tag
          color={color}
          icon={icon}
          onClick={(e) => {
            e.stopPropagation();
            handleCellClick(record, columnKey);
          }}
          style={{ cursor: 'pointer' }}
        >
          {count}
        </Tag>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: 'Control',
      dataIndex: 'control_code',
      key: 'control_code',
      width: 180,
      fixed: 'left',
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      title: 'Control Name',
      dataIndex: 'control_name',
      key: 'control_name',
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      title: 'Failures (Open)',
      dataIndex: 'failures_open',
      key: 'failures_open',
      width: 120,
      align: 'center',
      render: (count, record) => renderCountCell(count, record, 'failures_open'),
      sortable: true,
    },
    {
      title: 'Failures (In Progress)',
      dataIndex: 'failures_in_progress',
      key: 'failures_in_progress',
      width: 140,
      align: 'center',
      render: (count, record) => renderCountCell(count, record, 'failures_in_progress'),
      sortable: true,
    },
    {
      title: 'Failures (Closed)',
      dataIndex: 'failures_closed',
      key: 'failures_closed',
      width: 120,
      align: 'center',
      render: (count, record) => renderCountCell(count, record, 'failures_closed'),
      sortable: true,
    },
    {
      title: 'Warnings (Open)',
      dataIndex: 'warnings_open',
      key: 'warnings_open',
      width: 120,
      align: 'center',
      render: (count, record) => renderCountCell(count, record, 'warnings_open'),
      sortable: true,
    },
    {
      title: 'Warnings (Closed)',
      dataIndex: 'warnings_closed',
      key: 'warnings_closed',
      width: 120,
      align: 'center',
      render: (count, record) => renderCountCell(count, record, 'warnings_closed'),
      sortable: true,
    },
    {
      title: 'Any',
      dataIndex: 'any',
      key: 'any',
      width: 100,
      align: 'center',
      render: (count, record) => renderCountCell(count, record, 'any'),
      sortable: true,
    },
    {
      title: 'Due',
      dataIndex: 'due',
      key: 'due',
      width: 100,
      align: 'center',
      render: (count, record) => renderCountCell(count, record, 'due'),
      sortable: true,
    },
    {
      title: 'Overdue',
      dataIndex: 'overdue',
      key: 'overdue',
      width: 100,
      align: 'center',
      render: (count, record) => (
        count > 0 ? (
          <Button
            type="link"
            danger
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleCellClick(record, 'overdue');
            }}
            style={{ padding: 0, height: 'auto', fontWeight: 500 }}
          >
            {count}
          </Button>
        ) : (
          <span style={{ color: colors.text.disabled }}>0</span>
        )
      ),
      sortable: true,
    },
  ];

  return (
    <TrustGridPage
      title="Findings Summary"
      subtitle="Overview of findings grouped by control"
      breadcrumbs={breadcrumbs}
      showRefresh
      onRefresh={fetchSummary}
    >
      <ProjectSelector />

      {!selectedProjectId ? (
        <div style={{ textAlign: 'center', padding: spacing.xl }}>
          Please select a project to view findings summary
        </div>
      ) : loading ? (
        <LoadingState type="spinner" />
      ) : (
        <TGDataTable
          data={summary}
          columns={columns}
          rowKey="control_code"
          loading={loading}
          pagination="client"
          pageSize={20}
          showExport
          showColumnConfig
          showFilters
          exportFilename="findings-summary"
          presetKey="trustgrid.findings.summary"
          onRefresh={fetchSummary}
        />
      )}
    </TrustGridPage>
  );
}

export default FindingsSummary;
